import React, { useEffect, useMemo, useState } from 'react';
import { SelectField, Tag, TagGroup } from '@oneaudi/unified-web-components';
import { FilterWrapper, MobileFilterContainer, TagGroupContainer } from './FilterStyles';
import { useInitialState } from '../../context/InitialStateContext';
import { useAppContext } from '../../context/Context';
import type { MoxxCarlineGroup } from '../../../@types/MoxxTypes';
import { useTrackingManager } from '../../context/useTrackingManager';
import { VehicleType } from '../../../web/generated/onegraph';

interface FilterProps {
  onKeyDown: (event: KeyboardEvent) => void;
}

export const Filter: React.FC<FilterProps> = ({ onKeyDown }) => {
  const { bodyTypes, i18nMessages } = useInitialState();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { trackTagClick } = useTrackingManager();
  const {
    selectedBodyType,
    setSelectedBodyType,
    selectedVehicleType,
    setSelectedVehicleType,
    setFilteredCarlineGroups,
    setSelectedCarGroupId,
    selectedCarGroupId,
    logger,
    filterContainer,
  } = useAppContext();

  const { carlineGroups: unfilteredCarlineGroups, availableVehicleTypes } = useInitialState();

  const [markAsNew, setMarkAsNew] = useState(false);

  const truncateString = (inputString: string | undefined): string => {
    const maxLength = 20;
    if (!inputString) return '';

    if (inputString.length <= maxLength) {
      return inputString;
    }
    return `${inputString.slice(0, maxLength - 3)}...`;
  };

  const handleFilterChange = (
    filterType: 'bodyType' | 'vehicleType' | 'markAsNew',
    value?: string,
  ) => {
    if (
      filterType === 'bodyType' &&
      setSelectedBodyType &&
      setSelectedVehicleType &&
      setSelectedCarGroupId
    ) {
      setSelectedVehicleType(undefined);
      setMarkAsNew(false);
      setSelectedCarGroupId(undefined);
      setSelectedBodyType((prevSelected) => (prevSelected === value ? undefined : value));
    } else if (
      filterType === 'vehicleType' &&
      setSelectedVehicleType &&
      setSelectedBodyType &&
      setSelectedCarGroupId
    ) {
      setSelectedBodyType(undefined);
      setSelectedCarGroupId(undefined);
      setMarkAsNew(false);
      setSelectedVehicleType((prevSelected) => (prevSelected === value ? undefined : value));
    } else if (filterType === 'markAsNew') {
      setSelectedVehicleType?.(undefined);
      setSelectedBodyType?.(undefined);
      setSelectedCarGroupId?.(undefined);
      setMarkAsNew(false);
      setMarkAsNew((prev) => !prev);
    } else {
      logger?.warn('Filter change handler received an undefined setter function');
    }
  };

  const resetFilter = () => {
    if (setSelectedBodyType) {
      setSelectedBodyType(undefined);
    }
    if (setSelectedVehicleType) {
      setSelectedVehicleType(undefined);
    }
    if (setFilteredCarlineGroups) {
      setFilteredCarlineGroups(unfilteredCarlineGroups);
    }
    if (setSelectedCarGroupId) {
      setSelectedCarGroupId(undefined);
    }
    if (markAsNew) {
      setMarkAsNew(false);
    }
  };

  // check if carline groups have at least 1 new vehicle
  const hasNewCarline = unfilteredCarlineGroups.some((group) =>
    group.carlines.some((carline) => carline.markAsNew),
  );

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      onKeyDown(event);
    };
    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [selectedBodyType, selectedVehicleType]);

  const filteredCarlineGroups = useMemo(() => {
    return unfilteredCarlineGroups
      .map((carGroup) => {
        let matchingCarlines = carGroup.carlines;

        // Filter by body type if selected
        if (selectedBodyType) {
          matchingCarlines = matchingCarlines.filter((carline) =>
            carline.bodyTypes.includes(selectedBodyType),
          );
        }

        // Filter by vehicle type if selected
        if (selectedVehicleType) {
          matchingCarlines = matchingCarlines.filter(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            (carline) => carline.vehicleType === selectedVehicleType,
          );
        }
        // Filter by "new" if selected
        if (markAsNew) {
          matchingCarlines = matchingCarlines.filter((carline) => carline.markAsNew);
        }

        return matchingCarlines.length > 0 ? { ...carGroup, carlines: matchingCarlines } : null;
      })
      .filter((group): group is MoxxCarlineGroup => group !== null);
  }, [selectedBodyType, selectedVehicleType, unfilteredCarlineGroups, markAsNew]);

  // Collapse carline group in case its not included in next filter
  useEffect(() => {
    if (!selectedCarGroupId || !setSelectedCarGroupId) return;

    const filteredCarGroupIds = filteredCarlineGroups.map((group) => group.id);
    if (!filteredCarGroupIds.includes(selectedCarGroupId)) {
      setSelectedCarGroupId(undefined);
    }
  }, [filteredCarlineGroups]);

  // Update context
  useEffect(() => {
    setFilteredCarlineGroups?.(filteredCarlineGroups);
  }, [filteredCarlineGroups]);

  const bev = VehicleType.Bev; // electric
  const phev = VehicleType.Phev; // hybrid
  const icev = VehicleType.Icev; // petrol / diesel
  const electricText = truncateString(i18nMessages.electricFilter);
  const hybridText = truncateString(i18nMessages.hybridFilter);
  const petrolDieselText = truncateString(i18nMessages.petrolDiesel);
  const newText = truncateString(i18nMessages.markAsNew);
  const allModelsText = truncateString(i18nMessages.allModelsFilterText);

  const [selectedValue, setSelectedValue] = useState<string>(allModelsText);

  return (
    <FilterWrapper>
      <MobileFilterContainer>
        <SelectField
          id="filterType"
          label={i18nMessages.filterByLabel}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            const { selectedIndex } = e.target;
            const selectedOption = e.target.options[selectedIndex];
            const selectedValue = selectedOption.value;
            const selectedValueLabel = selectedOption.text;

            setSelectedValue(selectedValue);

            if (selectedValue === allModelsText) {
              resetFilter();
              trackTagClick(selectedValueLabel, selectedValue);
              return;
            }

            if (hasNewCarline && selectedValue === newText) {
              handleFilterChange('markAsNew');
              trackTagClick(selectedValueLabel, selectedValue);
              return;
            }

            if (availableVehicleTypes.includes(selectedValue as VehicleType)) {
              handleFilterChange('vehicleType', selectedValue);
              trackTagClick(selectedValueLabel, selectedValue);
              return;
            }

            // Body type selection
            const bodyTypeMatch = bodyTypes.find((b) => b[0] === selectedValue);
            if (bodyTypeMatch) {
              handleFilterChange('bodyType', selectedValue);
              trackTagClick(bodyTypeMatch[1], selectedValue);
            }
          }}
          value={selectedValue}
        >
          <option value={allModelsText} key="vehicle-all">
            {allModelsText}
          </option>

          {hasNewCarline && (
            <option value={newText} key="vehicle-new">
              {newText}
            </option>
          )}

          {/* First, display vehicle types */}
          {availableVehicleTypes.includes(bev) && (
            <option value={bev} key="vehicle-type-bev">
              {electricText}
            </option>
          )}
          {availableVehicleTypes.includes(phev) && (
            <option value={phev} key="vehicle-type-phev">
              {hybridText}
            </option>
          )}
          {availableVehicleTypes.includes(icev) && (
            <option value={icev} key="vehicle-type-icev">
              {petrolDieselText}
            </option>
          )}

          {/* Now, display body types */}
          {bodyTypes.map((bodyType, index) => (
            <option value={bodyType[0]} key={`body-${bodyType[0]}-${index}`}>
              {bodyType[1]}
            </option>
          ))}
        </SelectField>
      </MobileFilterContainer>
      <TagGroupContainer ref={filterContainer}>
        {/* initialy selected default tag */}
        <TagGroup size="sm">
          <Tag
            label={allModelsText}
            variant="selectable"
            value={allModelsText}
            onChange={() => {
              resetFilter();
              setSelectedValue(allModelsText);
              trackTagClick(allModelsText, allModelsText);
            }}
            checked={selectedValue === allModelsText}
          />
          {hasNewCarline && (
            <Tag
              label={newText}
              variant="selectable"
              value={newText}
              onChange={() => {
                if (selectedValue === newText) {
                  resetFilter();
                  setSelectedValue(allModelsText);
                } else {
                  handleFilterChange('markAsNew');
                  setSelectedValue(newText);
                  trackTagClick(newText, newText);
                }
              }}
              checked={selectedValue === newText}
            />
          )}

          {availableVehicleTypes.includes(bev) && (
            <Tag
              key="electro"
              icon="charging"
              data-testid="electro"
              role="button"
              label={electricText}
              aria-label={electricText}
              variant="selectable"
              onChange={() => {
                if (selectedValue === bev.toString()) {
                  resetFilter();
                  setSelectedValue(allModelsText);
                } else {
                  handleFilterChange('vehicleType', bev);
                  setSelectedValue(bev);
                  trackTagClick(electricText, bev);
                }
              }}
              value={bev}
              checked={selectedValue === bev.toString()}
            />
          )}

          {availableVehicleTypes.includes(phev) && (
            <Tag
              key="hybrid"
              data-testid="hybrid"
              label={hybridText}
              aria-label={hybridText}
              variant="selectable"
              onChange={() => {
                if (selectedValue === phev.toString()) {
                  resetFilter();
                  setSelectedValue(allModelsText);
                } else {
                  handleFilterChange('vehicleType', phev);
                  setSelectedValue(phev);
                  trackTagClick(hybridText, phev);
                }
              }}
              value={phev}
              checked={selectedValue === phev.toString()}
            />
          )}
          {availableVehicleTypes.includes(icev) && (
            <Tag
              key="petrolDiesel"
              data-testid="petrolDiesel"
              label={petrolDieselText}
              aria-label={petrolDieselText}
              variant="selectable"
              onChange={() => {
                if (selectedValue === icev.toString()) {
                  resetFilter();
                  setSelectedValue(allModelsText);
                } else {
                  handleFilterChange('vehicleType', icev);
                  setSelectedValue(icev);
                  trackTagClick(petrolDieselText, icev);
                }
              }}
              value={icev}
              checked={selectedValue === icev.toString()}
            />
          )}
          {bodyTypes.map((bodyType, index) => (
            <Tag
              key={`${bodyType[0]}-${index}`}
              data-testid={bodyType[0]}
              label={bodyType[1]}
              variant="selectable"
              onChange={() => {
                if (selectedValue === bodyType[0]) {
                  setSelectedValue(allModelsText);
                  resetFilter();
                } else {
                  handleFilterChange('bodyType', bodyType[0]);
                  setSelectedValue(bodyType[0]);
                  trackTagClick(bodyType[1], bodyType[0]);
                }
              }}
              value={bodyType[0]}
              checked={selectedValue === bodyType[0]}
            />
          ))}
        </TagGroup>
      </TagGroupContainer>
    </FilterWrapper>
  );
};
